/**
 * Navigation Item Button Component
 *
 * @module shared/components/navigation/NavItemButton
 *
 * Single navigation item button with tooltip support
 */

import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import { memo } from 'react';

import type { NavItem } from './types';

interface NavItemButtonProps {
  item: NavItem;
  collapsed: boolean;
  isActive: boolean;
  onClick: () => void;
}

export const NavItemButton = memo(function NavItemButton({
  item,
  collapsed,
  isActive,
  onClick,
}: NavItemButtonProps) {
  const muiTheme = useMuiTheme();

  return (
    <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
      <ListItemButton
        onClick={onClick}
        selected={isActive}
        sx={{
          borderRadius: 1.5,
          mb: 0.125,
          minHeight: 30,
          px: collapsed ? 1 : 1.25,
          justifyContent: collapsed ? 'center' : 'flex-start',
          '&.Mui-selected': {
            bgcolor: alpha(muiTheme.palette.primary.main, 0.12),
            color: 'primary.main',
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
            '&:hover': {
              bgcolor: alpha(muiTheme.palette.primary.main, 0.16),
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 28,
            justifyContent: 'center',
            color: isActive ? 'primary.main' : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.6875rem',
              fontWeight: isActive ? 600 : 500,
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
});
